import { FC, ReactNode } from 'react';

import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { DividendFrequency, DividendPayout } from '../../../hooks/dividend/dividend.types';
import { useShareClasses } from '../../../hooks/share-class/use-share-classes';
import { useFormat } from '../../../hooks/use-format.hook';
import { getTranslation } from '../../../utils/get-translation.util';
import { formatDividendAmount } from '../utils/format-dividend-amount.util';
import { DividendStatusTag } from './dividend-status-tag';

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const FREQUENCY_LABELS: Record<DividendFrequency, string> = {
  [DividendFrequency.ANNUALLY]: getTranslation('ANNUAL'),
  [DividendFrequency.MONTHLY]: getTranslation('MONTHLY'),
  [DividendFrequency.QUARTERLY]: getTranslation('DIVIDEND_FREQUENCY_QUARTERLY'),
  [DividendFrequency.SEMI_ANNUALLY]: getTranslation('DIVIDEND_FREQUENCY_SEMI_ANNUAL'),
};

const formatPeriod = (
  frequency: DividendFrequency,
  year: number,
  periodIndex: null | number,
): string => {
  if (frequency === DividendFrequency.MONTHLY && periodIndex !== null)
    return `${MONTH_NAMES[periodIndex - 1]} ${year}`;
  if (frequency === DividendFrequency.QUARTERLY && periodIndex !== null)
    return `Q${periodIndex} ${year}`;
  if (frequency === DividendFrequency.SEMI_ANNUALLY && periodIndex !== null)
    return `H${periodIndex} ${year}`;
  return `${year}`;
};

const CardRow: FC<{ label: ReactNode; value: ReactNode }> = ({ label, value }) => (
  <div className="flex items-start justify-between gap-3 py-3">
    <span className="shrink-0 text-sm font-[450] text-gray-500">{label}</span>
    <span className="min-w-0 break-words text-right text-sm text-gray-700">{value}</span>
  </div>
);

type Props = {
  companyId: string;
  currencySymbol: string;
  payout: DividendPayout;
};

// Mobile is view-only: read-only status tag, plain dates, no actions or inline editing.
export const DividendPayoutCard: FC<Props> = ({ companyId, currencySymbol, payout }) => {
  const { shareClasses } = useShareClasses({ companyId, filter: 'all', take: 200 });
  const { format } = useFormat();

  const totalPayout = payout.items.reduce((sum, item) => sum + (item.totalAmount ?? 0), 0);

  const shareClassNames = Array.from(
    new Set(payout.items.map((item) => item.shareClassId).filter(Boolean)),
  )
    .map((id) => shareClasses.find((sc) => sc.id === id)?.name)
    .filter((name): name is string => Boolean(name));

  const shareClassLabel =
    shareClassNames.length === 0
      ? '—'
      : shareClassNames.slice(0, 3).join(', ') +
        (shareClassNames.length > 3 ? ` +${shareClassNames.length - 3}` : '');

  const formatDate = (date?: null | string) => (date ? format(new Date(date), 'dd/MM/yyyy') : '—');

  return (
    <div className="flex flex-col rounded-lg border border-gray-100 bg-white px-3 shadow-sm">
      <div className="flex items-center justify-between gap-2 border-b border-gray-100 py-4">
        <span className="min-w-0 break-words text-sm font-[550] text-gray-700">{payout.name}</span>
        <div className="shrink-0">
          <DividendStatusTag status={payout.status} />
        </div>
      </div>

      <div className="flex flex-col divide-y divide-gray-100">
        <CardRow label={<AppFormattedMessage id="SHARE_CLASS" />} value={shareClassLabel} />
        <CardRow
          label={<AppFormattedMessage id="FREQUENCY" />}
          value={FREQUENCY_LABELS[payout.frequency]}
        />
        <CardRow
          label={<AppFormattedMessage id="PERIOD" />}
          value={formatPeriod(payout.frequency, payout.year, payout.periodIndex)}
        />
        <CardRow
          label={<AppFormattedMessage id="TOTAL_PAYOUT" />}
          value={totalPayout > 0 ? `${currencySymbol}${formatDividendAmount(totalPayout)}` : '—'}
        />
        <CardRow
          label={<AppFormattedMessage id="APPROVAL_DATE" />}
          value={formatDate(payout.approvalDate)}
        />
        <CardRow
          label={<AppFormattedMessage id="PAYOUT_DATE" />}
          value={formatDate(payout.payoutDate)}
        />
      </div>
    </div>
  );
};
